'use client';

import { ParsedUrlQuery } from 'querystring';

import { useState, SyntheticEvent } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { StarOutline } from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  Stack,
  AvatarGroup,
  Avatar,
  Tabs,
  Tab,
  Select,
  MenuItem,
  Button,
  ButtonBase,
  Link,
} from '@mui/material';
import {
  Image,
  ClubLogo,
  EventCard,
  CompetitionRanking,
  CompetitionRankingRow,
  // Ads,
} from '@scorenco/components';
import {
  RouteLink,
  useRouteInfo,
  usePaginatedQuery,
  ssrApolloService,
  Types,
  addSize,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalAdArea,
  NextOptiDigitalConfig,
  NextPageWithLayout,
} from '@scorenco/next';
import { isToday } from 'date-fns';

import {
  GroupedEvents,
  JoinClub,
  PageLayout,
  PageSeo,
  SportChip,
  TabPanel,
} from '@/components';
import { useAppRedirect } from '@/hooks/useAppRedirect';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport, extractBase36Id } from '@/utils';

import {
  GetMatchsDocument,
  GetMatchsQuery,
  GetMatchsQueryVariables,
  WebOneLastEventByTeamDocument,
  WebOneNextEventByTeamDocument,
  WebTeamGetInfosDocument,
} from './_internal/Team.query.generated';

const extractUrlInfo = (query: ParsedUrlQuery) => ({
  teamId: extractBase36Id(query.team),
  clubId: extractBase36Id(query.club),
  sportSlug: typeof query.sport === 'string' ? query.sport : undefined,
});

const getDateInput = (value: unknown): string | number | Date | undefined => {
  if (typeof value === 'string' || typeof value === 'number') {
    return value;
  }

  return value instanceof Date ? value : undefined;
};

const Team: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({ sport }) => {
  const routeInfo = useRouteInfo();
  const urlInfo = extractUrlInfo(routeInfo.query);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedCompetition, setSelectedCompetition] = useState(0);
  useAppRedirect();

  const handleChangeTab = (_event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const teamQuery = useQuery(WebTeamGetInfosDocument, {
    variables: { teamId: urlInfo.teamId as number },
    skip: !urlInfo.teamId,
    context: {
      role: 'anonymous',
    },
  });

  const team = teamQuery.data?.teams_team_detail_by_team_id?.[0];
  const sportSlug = routeInfo.query.sport;

  const getResults = usePaginatedQuery<GetMatchsQuery, GetMatchsQueryVariables>(
    GetMatchsDocument,
    undefined,
    {
      variables: {
        teamId: team?.team_id,
        dateFilter: -1,
        limit: 10,
      },
      skip: !team?.team_id,
      context: {
        role: 'anonymous',
      },
    }
  );

  const getNextMatchs = usePaginatedQuery<
    GetMatchsQuery,
    GetMatchsQueryVariables
  >(GetMatchsDocument, undefined, {
    variables: {
      teamId: team?.team_id,
      dateFilter: 1,
      limit: 10,
    },
    skip: !team?.team_id,
    context: {
      role: 'anonymous',
    },
  });

  const lastEventQuery = useQuery(WebOneLastEventByTeamDocument, {
    variables: { teamId: urlInfo.teamId as number },
    skip: !urlInfo.teamId,
    context: {
      role: 'anonymous',
    },
  });

  const lastEvent =
    lastEventQuery.data?.competitions_event_detail_by_team_id?.[0];
  const lastEventDate = getDateInput(lastEvent?.date);

  const nextEventQuery = useQuery(WebOneNextEventByTeamDocument, {
    variables: { teamId: urlInfo.teamId as number },
    skip: !urlInfo.teamId,
    context: {
      role: 'anonymous',
    },
  });

  const nextEvent =
    nextEventQuery.data?.competitions_event_detail_by_team_id?.[0];

  const competition = team?.competitions?.[0]?.competition;

  return (
    <>
      <PageSeo
        title={`${team?.name_in_competition} - ${sportSlug} - Score'n'co`}
        description={`Suivez l'équipe ${team?.name_in_competition} sur Score'n'co 🔵🟢. Ne ratez aucun résultat de l'équipe ${team?.name_in_club} du club ${team?.club?.[0]?.club?.short_name} ainsi que toutes les autres équipes du club.`}
        sharedImage={team?.team?.share_picture}
        url={team?.url}
      />
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/equipe" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <PageLayout
        image={
          team?.team_in_season?.squad_picture
            ? team.team_in_season.squad_picture
            : sport?.image
        }
        breadcrumbs={[
          {
            label: sport?.name || '',
            href: {
              routeKey: RouteKey.Sport,
              params: { sport: sportSlug },
            },
          },
          {
            label: 'Clubs',
            href: {
              routeKey: RouteKey.Clubs,
              params: { sport: sportSlug },
            },
          },
          team?.club?.[0]?.equipments?.[0]?.place?.city?.department?.code
            ? {
                label:
                  team?.club?.[0]?.equipments?.[0]?.place?.city?.department
                    ?.name || '',

                href: {
                  routeKey: RouteKey.Clubs,
                  params: {
                    sport: sportSlug,
                    department:
                      team?.club?.[0]?.equipments?.[0]?.place?.city?.department
                        ?.code,
                  },
                },
              }
            : undefined,
          {
            label: team?.club?.[0]?.name || '',
            href: {
              routeKey: RouteKey.Club,
              params: {
                sport: sportSlug,
                club: `${team?.club?.[0]?.slug}-${team?.club?.[0]?.id.toString(
                  36
                )}`,
              },
            },
          },
          {
            label: team?.name_in_club || '',
            current: true,
          },
        ]}
      >
        <Stack
          gap={2}
          direction={{ xs: 'column', md: 'row' }}
          alignItems="stretch"
        >
          <Paper
            variant="bordered"
            sx={{
              alignItems: 'stretch',
              justifyContent: 'center',
              flex: 2,
            }}
          >
            <Stack direction="row" gap={4} alignItems="center">
              <ClubLogo
                logo={team?.logo}
                height={96}
                width={96}
                css={(theme) => css`
                  width: 6rem;
                  height: 6rem;

                  ${theme.breakpoints.up('md')} {
                    width: 6rem;
                    height: 6rem;
                  }
                `}
              />
              <Stack
                component="h1"
                sx={{
                  flexDirection: 'column',
                  alignItems: 'start',
                  m: 0,
                }}
              >
                <SportChip
                  sport={sport?.slug}
                  sx={{
                    mb: 2,
                  }}
                />
                <Typography component="span" variant="h2">
                  {team?.name_in_club}
                </Typography>
                <Typography
                  component={RouteLink}
                  href={team?.club?.[0]?.club?.url}
                  variant="h6"
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                    mb: '0.5rem',
                  }}
                >
                  {team?.club?.[0]?.name}
                </Typography>
              </Stack>
            </Stack>
          </Paper>
          {team?.team_in_season?.squad_picture && (
            <Image
              src={team.team_in_season.squad_picture}
              css={(theme) => css`
                width: 100%;
                aspect-ratio: 3/2;
                object-fit: cover;
                border-radius: 0.5rem;
                background-color: ${theme.vars.palette.background.paper};

                ${theme.breakpoints.up('md')} {
                  width: 33%;
                }
              `}
              width={900}
              height={900}
            />
          )}
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          py={2}
          gap={2}
          alignItems="center"
        >
          {team?.team?.fans?.length ? (
            <>
              <AvatarGroup max={3}>
                {team?.team?.fans?.slice(0, 3).map((fan) => (
                  <Avatar
                    key={fan?.fan?.id}
                    sx={{ width: 34, height: 34 }}
                    alt={fan?.fan?.name}
                    src={addSize(fan?.fan?.picture_url, 32, 32)}
                  >
                    {fan?.fan?.name?.slice(0, 1).toUpperCase()}
                  </Avatar>
                ))}
              </AvatarGroup>
              <Typography variant="h4" flex={1}>
                {team?.team?.fans?.length} fans
              </Typography>
            </>
          ) : (
            <Typography variant="h4" flex={1}>
              Aucun fan, soyez le premier !
            </Typography>
          )}
          <Button
            component={RouteLink}
            href={[
              `${process.env.NEXT_PUBLIC_MOBILE_URL}/auth/select-sport`,
              typeof window !== 'undefined' &&
                `redirectTo=${encodeURIComponent(window.location.href)}`,
            ]
              .filter(Boolean)
              .join('?')}
            startIcon={<StarOutline />}
            color="secondary"
            variant="contained"
          >
            Devenir fan
          </Button>
        </Stack>

        {/* Test pour ces 2 clubs, département du Rhone et clubs du groupe C de D1 en Loire-Atlantique voir avec Lucas */}
        {urlInfo.clubId === 124304 || urlInfo.clubId === 122563 ? (
          <iframe
            src={`https://widgets.scorenco.com/auto/sponsors-slider/${urlInfo.clubId}?width=31&height=15.5`}
            style={{
              height: '17.5rem',
              overflow: 'hidden',
              width: '100%',
              border: 'none',
            }}
            scrolling="no"
          ></iframe>
        ) : team?.club?.[0]?.club?.department?.code === '69' &&
          sportSlug === 'football' ? (
          <a
            href="https://calendly.com/sdsport09"
            target="_blank"
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              flex-direction: row;
              justify-content: center;
            `}
            rel="noreferrer noopener"
          >
            <Image
              src="/images/scorenco_operation.png"
              width="750px"
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
                maxHeight: '187.5px',
              }}
            />
          </a>
        ) : competition?.id === 557175 ? (
          <a
            target="_blank"
            rel="noreferrer noopener"
            css={css`
              width: 100%;
              display: flex;
              align-items: center;
              flex-direction: row;
              justify-content: center;
            `}
            href="mailto:laisismoise@gmail.com"
          >
            <Image
              src="/images/pubs/artisan-lam.jpg"
              width="750px"
              style={{
                maxWidth: '100%',
                objectFit: 'contain',
                maxHeight: '187.5px',
              }}
            />
          </a>
        ) : (
          <>
            {/* TODO: Comment this when we have a campaign */}
            <NextAdArea
              akceloSlotId="Scorenco_PlayRVideo"
              akceloAdUnitClass="akcelo-adunit-15786"
              withGooglePublisherTag
            />
            <NextOptiDigitalAdArea areaId="Billboard_1" />
            <NextOptiDigitalAdArea areaId="Mobile_Pos1" />
            {/* {sport?.id && (
              <Ads
                type="team"
                sport_id={sport?.id}
                department_id={team?.club?.[0]?.club?.department?.id}
                club_name={team?.name_in_competition}
              />
            )} */}
          </>
        )}

        <Stack
          gap={2}
          direction="row"
          width="100%"
          sx={{
            '@media screen and (max-width: 1040px)': {
              flexDirection: 'column',
            },
            mt: 4,
          }}
        >
          <Stack
            spacing={3}
            width="100%"
            sx={{
              '@media screen and (max-width: 1040px)': {
                mb: 2,
              },
            }}
          >
            <Paper variant="bordered">
              <Typography
                variant="h5"
                sx={{ fontStyle: 'unset', textTransform: 'uppercase', mb: 1 }}
              >
                {lastEvent?.status === 'live'
                  ? 'Match en direct'
                  : lastEventDate && isToday(new Date(lastEventDate))
                    ? 'Match du jour'
                    : 'Dernier match'}
              </Typography>
              {lastEvent ? (
                <RouteLink href={lastEvent?.url}>
                  <EventCard event={lastEvent} />
                </RouteLink>
              ) : (
                <Box sx={{ padding: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{ width: '100%', textAlign: 'center' }}
                  >
                    Aucun résultat de disponible pour le moment. 😔
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
          <Stack
            spacing={3}
            width="100%"
            sx={{
              '@media screen and (max-width: 1040px)': {
                m: '0 !important',
              },
            }}
          >
            <Paper variant="bordered">
              <Typography
                variant="h5"
                sx={{ fontStyle: 'unset', textTransform: 'uppercase', mb: 1 }}
              >
                Prochain match
              </Typography>
              {nextEvent ? (
                <RouteLink href={nextEvent?.url}>
                  <EventCard event={nextEvent} />
                </RouteLink>
              ) : (
                <Box sx={{ padding: 5 }}>
                  <Typography
                    variant="h5"
                    sx={{ width: '100%', textAlign: 'center' }}
                  >
                    Repos, pas de match à venir ! 😎
                  </Typography>
                </Box>
              )}
            </Paper>
          </Stack>
        </Stack>
        <Stack
          spacing={2}
          direction="row"
          width="100%"
          sx={{
            '@media screen and (max-width: 700px)': {
              flexDirection: 'column',
            },
            mt: 4,
          }}
        >
          <Stack
            spacing={3}
            width="100%"
            sx={{
              '@media screen and (max-width: 700px)': {
                mb: 2,
              },
            }}
          >
            <Stack spacing={2}>
              <Typography variant="h4">Matchs</Typography>
              <Select
                fullWidth
                sx={{ backgroundColor: 'white' }}
                name="selectedCompetition"
                onChange={(e) => {
                  setSelectedCompetition(e.target.value);
                }}
                value={selectedCompetition}
              >
                <MenuItem value={0}>Toutes les compétitions</MenuItem>
                {team?.competitions?.map((competition) => (
                  <MenuItem
                    key={competition?.competition?.id}
                    value={competition?.competition?.id}
                  >
                    {competition?.competition?.name}
                  </MenuItem>
                ))}
              </Select>
              <Paper variant="bordered" sx={{ padding: 0 }}>
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    width: '100%',
                  }}
                >
                  <Tabs
                    value={currentTab}
                    onChange={handleChangeTab}
                    centered
                    variant="fullWidth"
                  >
                    <Tab label="Résultats" />
                    <Tab label="Calendrier" />
                  </Tabs>
                </Box>
                <TabPanel
                  value={currentTab}
                  index={0}
                  style={{ width: '100%' }}
                >
                  {(getResults?.list?.length ?? 0) > 0 && (
                    <GroupedEvents
                      query={getResults}
                      showShortName={true}
                      showCompetition={true}
                      competitionId={selectedCompetition}
                    />
                  )}
                  {getResults?.list?.length === 0 && (
                    <Box sx={{ padding: 5 }}>
                      <Typography
                        variant="h5"
                        sx={{ width: '100%', textAlign: 'center' }}
                      >
                        Aucun résultat de disponible pour le moment. 😔
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
                <TabPanel
                  value={currentTab}
                  index={1}
                  style={{ width: '100%' }}
                >
                  {(getNextMatchs?.list?.length ?? 0) > 0 && (
                    <GroupedEvents
                      query={getNextMatchs}
                      showShortName={true}
                      showCompetition={true}
                      competitionId={selectedCompetition}
                    />
                  )}
                  {getNextMatchs?.list?.length === 0 && (
                    <Box sx={{ padding: 5 }}>
                      <Typography
                        variant="h5"
                        sx={{ width: '100%', textAlign: 'center' }}
                      >
                        Repos, pas de match à venir ! 😎
                      </Typography>
                    </Box>
                  )}
                </TabPanel>
              </Paper>
            </Stack>
            <Box
              component={RouteLink}
              href={`${process.env.NEXT_PUBLIC_MOBILE_URL}/clubs/${urlInfo.clubId}`}
            >
              <JoinClub />
            </Box>
            <NextOptiDigitalAdArea areaId="Mobile_Pos2" />
          </Stack>

          <Stack
            gap={3}
            width="100%"
            sx={{
              '@media screen and (max-width: 700px)': {
                m: '0 !important',
              },
            }}
          >
            {competition?.rankings?.length !== 0 && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h4">
                    Classement {team?.club?.[0]?.club?.short_name}
                  </Typography>
                  <Link component={RouteLink} href={competition?.url}>
                    <Typography variant="body2">{competition?.name}</Typography>
                  </Link>
                </Box>
                <Paper
                  variant="bordered"
                  sx={{ p: 0, alignItems: 'stretch', overflow: 'hidden' }}
                >
                  <CompetitionRanking
                    competitionId={competition?.id}
                    rankings={competition?.rankings}
                    highlightTeams={
                      team?.team_in_season?.id
                        ? [team.team_in_season?.id]
                        : undefined
                    }
                    sport={sport}
                    slots={{
                      row: ({ ranking, ...rest }) => (
                        <ButtonBase
                          component={RouteLink}
                          href={ranking?.url}
                          sx={{ display: 'block' }}
                        >
                          <CompetitionRankingRow ranking={ranking} {...rest} />
                        </ButtonBase>
                      ),
                    }}
                  />
                </Paper>
              </Stack>
            )}
          </Stack>
        </Stack>

        <NextOptiDigitalAdArea areaId="Billboard_Bottom" />
        <NextOptiDigitalAdArea areaId="Mobile_Bottom" />
      </PageLayout>
    </>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    if (query['no-ssr'] === undefined) {
      const urlInfo = extractUrlInfo(query);

      if (urlInfo.clubId && urlInfo.sportSlug && urlInfo.teamId) {
        const teamQuery = await apolloClient.query({
          query: WebTeamGetInfosDocument,
          variables: { teamId: urlInfo.teamId },
        });
        const team = teamQuery.data?.teams_team_detail_by_team_id?.[0];

        if (team) {
          const sport = await getSport(urlInfo.sportSlug);

          return addApolloState({
            ...previous,
            props: {
              ...previous.props,
              sport,
            },
          });
        }
      }

      return { notFound: true };
    }

    return addApolloState(previous);
  },
]);

Team.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Team;
